/**
 * TopTippersCard.jsx — small card listing the scribe's top tippers.
 *
 * Shows the top 10 senders by coins_sent within the selected window
 * (default 7 days). Auto-hides when the scribe has zero donations in
 * the 7-day window.
 *
 * Scribe-only affordances:
 *   • "Thank" button per row → ThankYouDialog → POST /api/coins/donations/thank
 *   • "Thank everyone" button (window-aware) → /api/coins/donations/thank-all
 *   • For anonymous senders (no resolvable user) the per-row CTA flips to
 *     "Public shoutout" and opens a small inline composer that posts to
 *     /api/coins/writer/shoutouts.
 */
import React, { useEffect, useState } from 'react';
import { Heart, Loader2, Crown, Medal, Megaphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';
import ThankYouDialog from './ThankYouDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WINDOW_OPTIONS = [
  { days: 7,  label: 'This week' },
  { days: 30, label: 'This month' },
  { days: 90, label: 'Last 90 days' },
];

const isAnon = (t) => !t.username || t.username === 'Anonymous';

const TopTippersCard = ({ onShoutoutCreated }) => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [tippers, setTippers] = useState([]);

  // single-row thank dialog
  const [thankTarget, setThankTarget] = useState(null);     // tipper row | null
  const [thankBusy, setThankBusy] = useState(false);

  // bulk thank dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // shoutout composer (for anon row)
  const [shoutoutOpen, setShoutoutOpen] = useState(false);
  const [shoutoutText, setShoutoutText] = useState('');
  const [shoutoutBusy, setShoutoutBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/api/coins/donations/top-tippers?days=${days}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setTippers(d.tippers || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  // Hide entirely on the default window if there are no donations yet.
  if (!loading && tippers.length === 0 && days === 7) {
    return null;
  }

  const sendThanks = async (payload) => {
    if (!thankTarget) return;
    setThankBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/coins/donations/thank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender_user_id: thankTarget.sender_user_id, ...payload }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(`Thanks sent to ${thankTarget.username || 'tipper'}`);
      setThankTarget(null);
    } catch (err) {
      toast.error(err.message || 'Failed to send thanks');
    } finally {
      setThankBusy(false);
    }
  };

  const sendBulkThanks = async (payload) => {
    setBulkBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/coins/donations/thank-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days, ...payload }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      if (data.sent === 0) {
        toast.message('No reachable tippers in this window — try a public shoutout instead.');
      } else {
        toast.success(`Sent thanks to ${data.sent} tipper${data.sent === 1 ? '' : 's'}.`);
      }
      setBulkOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to send thanks');
    } finally {
      setBulkBusy(false);
    }
  };

  const submitShoutout = async () => {
    const msg = shoutoutText.trim();
    if (!msg) { toast.error('Write something first.'); return; }
    setShoutoutBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/coins/writer/shoutouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success('Shoutout posted on your profile.');
      setShoutoutOpen(false);
      setShoutoutText('');
      if (onShoutoutCreated) onShoutoutCreated(data);
    } catch (err) {
      toast.error(err.message || 'Failed to post shoutout');
    } finally {
      setShoutoutBusy(false);
    }
  };

  const reachableCount = tippers.filter((t) => !isAnon(t)).length;
  const anyAnon = tippers.some(isAnon);

  return (
    <Card className="bg-slate-800/50 border-pink-500/30" data-testid="top-tippers-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-400" />
            Top tippers
          </span>
          <div className="flex items-center gap-1">
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                onClick={() => setDays(opt.days)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                  days === opt.days
                    ? 'bg-pink-500/20 border-pink-400 text-pink-200'
                    : 'border-slate-600 text-slate-400 hover:text-white'
                }`}
                data-testid={`top-tippers-window-${opt.days}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {loading ? (
          <div className="py-4 text-center text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : tippers.length === 0 ? (
          <div className="py-4 text-center text-gray-500 text-sm">
            No tips in this window yet. Readers can tip you from any book page.
          </div>
        ) : (
          <>
            {/* Bulk-thank action — only when at least one reachable user */}
            {reachableCount > 0 && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkOpen(true)}
                  className="border-pink-500/40 text-pink-200 hover:bg-pink-500/10 gap-1.5"
                  data-testid="thank-all-btn"
                >
                  <Heart className="w-4 h-4" />
                  Thank everyone ({reachableCount})
                </Button>
              </div>
            )}

            <ol className="space-y-1.5" data-testid="top-tippers-list">
              {tippers.map((t, idx) => {
                const rank = idx + 1;
                const RankIcon = rank === 1 ? Crown : rank <= 3 ? Medal : null;
                const rankColour = rank === 1 ? 'text-amber-300'
                  : rank === 2 ? 'text-slate-300'
                  : rank === 3 ? 'text-orange-400'
                  : 'text-slate-500';
                const anon = isAnon(t);
                return (
                  <li
                    key={t.sender_user_id}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-900/40 border border-slate-700"
                    data-testid={`top-tippers-row-${rank}`}
                  >
                    <div className={`flex items-center justify-center w-7 h-7 shrink-0 ${rankColour}`}>
                      {RankIcon ? <RankIcon className="w-5 h-5" /> : <span className="font-bold text-xs">{rank}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{t.username || 'Anonymous'}</div>
                      <div className="text-[11px] text-gray-400">
                        {t.donation_count} tip{t.donation_count === 1 ? '' : 's'} · received{' '}
                        <span className="text-pink-300 font-semibold">{t.coins_received_total}🪙</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-pink-200">{t.coins_sent_total}🪙</div>
                      <div className="text-[10px] text-gray-500">sent</div>
                    </div>
                    {anon ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShoutoutOpen(true)}
                        className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 gap-1"
                        data-testid={`shoutout-btn-${rank}`}
                        title="Anonymous tipper — post a public shoutout instead"
                      >
                        <Megaphone className="w-3.5 h-3.5" />
                        Shoutout
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setThankTarget(t)}
                        className="border-pink-500/40 text-pink-200 hover:bg-pink-500/10 gap-1"
                        data-testid={`thank-btn-${rank}`}
                      >
                        <Heart className="w-3.5 h-3.5" />
                        Thank
                      </Button>
                    )}
                  </li>
                );
              })}
            </ol>

            {/* Helper note when only anon tippers are present */}
            {reachableCount === 0 && anyAnon && (
              <p className="text-[11px] text-amber-200/80 pt-1">
                All tippers in this window are anonymous — use a public shoutout to thank them.
              </p>
            )}
          </>
        )}
      </CardContent>

      {/* Single-row thank dialog */}
      <ThankYouDialog
        open={!!thankTarget}
        onOpenChange={(v) => { if (!v) setThankTarget(null); }}
        recipientName={thankTarget?.username}
        busy={thankBusy}
        onSubmit={sendThanks}
        title="Send a thank-you"
      />

      {/* Bulk-thank dialog (re-uses ThankYouDialog) */}
      <ThankYouDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        recipientName={`${reachableCount} tipper${reachableCount === 1 ? '' : 's'} in ${WINDOW_OPTIONS.find((o) => o.days === days)?.label.toLowerCase()}`}
        busy={bulkBusy}
        onSubmit={sendBulkThanks}
        title="Thank everyone"
        description="One thank-you, sent to every reachable tipper in the selected window."
      />

      {/* Inline shoutout composer */}
      {shoutoutOpen && (
        <div
          className="px-4 pb-4"
          data-testid="shoutout-composer"
        >
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold">
              <Megaphone className="w-4 h-4" />
              Public shoutout
            </div>
            <textarea
              value={shoutoutText}
              onChange={(e) => setShoutoutText(e.target.value.slice(0, 240))}
              rows={3}
              placeholder="Thanks to my anonymous tippers — you keep me writing!"
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              data-testid="shoutout-input"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-slate-400">{shoutoutText.length}/240 — visible on your writer profile</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShoutoutOpen(false); setShoutoutText(''); }}
                  className="border-slate-600 text-slate-200"
                  data-testid="shoutout-cancel"
                  disabled={shoutoutBusy}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={submitShoutout}
                  disabled={shoutoutBusy || !shoutoutText.trim()}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  data-testid="shoutout-submit"
                >
                  {shoutoutBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post shoutout'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TopTippersCard;
